import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import TimeRecordsTable from "./TimeRecordsTable";
import TimeChart from "./TimeChart";
import SettingsPage from "./SettingsPage";
import TimesheetsPage from "./TimesheetsPage";
import OutlookPage from "./OutlookPage";
import ProfilePage from "./ProfilePage";
import ReportsPage from "./ReportsPage";
import ProjectOverview from "./ProjectOverview";
import ResourceOverview from "./ResourceOverview";
import ProjectBillable from "./ProjectBillable";
import ClientSupport from "./ClientSupport";
import ResourcePTO from "./ResourcePTO";
import ResourceSplit from "./ResourceSplit";
import JiraPage from "./JiraPage";
import AnalyticsPage from "./AnalyticsPage";
import CloudDashboard from "./CloudDashboard";
import BitaDashboard from "./BitaDashboard";
import apiFetch from "./utils/api";
import { useAuth } from "./context/AuthContext";
import "./MainPage.css";
import "react-datepicker/dist/react-datepicker.css";

const bannerUrl = "/cor-logo-medium-res-white.webp";

const panels = Object.freeze({
  settings: 0,
  profile: 1,
  reports: 2,
  timesheets: 3,
  outlook: 4,
  jira: 5,
  dashboard: 6,
  cloudDashboard: 7,
  bitaDashboard: 8,
});

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const getEndOfWeek = (startDate) => {
  const end = new Date(startDate);
  end.setDate(end.getDate() + 6);
  return new Date(end.getFullYear(), end.getMonth(), end.getDate());
};

const formatDateRange = (start, end) => {
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return `${start.toLocaleDateString("en-GB", options)} – ${end.toLocaleDateString(
    "en-GB",
    options,
  )}`;
};

const DEFAULT_INACTIVITY_MS = 15 * 60 * 1000;

const canAccessAnalytics = (role) => {
  const normalized = String(role || "")
    .trim()
    .toUpperCase();
  return normalized === "TEAM ADMIN" || normalized === "SENIOR ADMIN";
};
const canAccessReports = (role) => {
  const normalized = String(role || "")
    .trim()
    .toUpperCase();
  return (
    normalized === "SUPERVISOR" ||
    normalized === "TEAM ADMIN" ||
    normalized === "SENIOR ADMIN"
  );
};
const canAccessSettings = (role) =>
  String(role || "")
    .trim()
    .toUpperCase() === "SENIOR ADMIN";
const canAccessResourcePto = (role) => {
  const normalized = String(role || "")
    .trim()
    .toUpperCase();
  return normalized === "TEAM ADMIN" || normalized === "SENIOR ADMIN";
};
const canAccessCloudDashboard = (role) =>
  String(role || "")
    .trim()
    .toUpperCase() === "SENIOR ADMIN";

const MainPage = () => {
  const navigate = useNavigate();
  const { user, setUser, getUser, logout } = useAuth();

  const activityTimer = useRef(null);
  const lastActiveRef = useRef(
    Number(localStorage.getItem("lastActive")) || Date.now(),
  );
  const inactivityMsRef = useRef(DEFAULT_INACTIVITY_MS);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showReports, setShowReports] = useState(false);

  const [reportsView, setReportsView] = useState("summary");
  const [showTimesheets, setShowTimesheets] = useState(false);
  const [showOutlook, setShowOutlook] = useState(false);
  const [showJira, setShowJira] = useState(false);
  const [showCloudDashboard, setShowCloudDashboard] = useState(false);
  const [showBitaDashboard, setShowBitaDashboard] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [timesheetsOpen, setTimesheetsOpen] = useState(false);
  const [timesheetsInitialDate, setTimesheetsInitialDate] = useState(null);

  const reportsEnabled = canAccessReports(user?.UserRole);
  const analyticsEnabled = canAccessAnalytics(user?.UserRole);
  const settingsEnabled = canAccessSettings(user?.UserRole);
  const resourcePtoEnabled = canAccessResourcePto(user?.UserRole);
  const cloudDashboardEnabled = canAccessCloudDashboard(user?.UserRole);

  const weekStart = useMemo(() => getStartOfWeek(selectedDate), [selectedDate]);
  const weekEnd = useMemo(() => getEndOfWeek(weekStart), [weekStart]);
  const weekStartMs = weekStart ? weekStart.getTime() : 0;
  const weekEndMs = weekEnd ? weekEnd.getTime() : 0;

  function selectShown(panel) {
    setShowSettings(panel === panels.settings);
    setShowProfile(panel === panels.profile);
    setShowReports(panel === panels.reports);
    setShowTimesheets(panel === panels.timesheets);
    setShowOutlook(panel === panels.outlook);
    setShowJira(panel === panels.jira);
    setShowCloudDashboard(panel === panels.cloudDashboard);
    setShowBitaDashboard(panel === panels.bitaDashboard);
    setDashboardOpen(panel === panels.dashboard);
  }

  useEffect(() => {
    (async () => {
      await getUser();
    })();
  }, [getUser]);

  useEffect(() => {
    if (!user) return undefined;

    (async () => {
      try {
        const cfg = await apiFetch("/api/config");
        if (cfg && typeof cfg.sessionTimeoutMin === "number") {
          inactivityMsRef.current = cfg.sessionTimeoutMin * 60 * 1000;
          return;
        }
      } catch (e) {}

      try {
        const me = await apiFetch("/api/me");
        if (me && typeof me.sessionTimeoutMin === "number") {
          inactivityMsRef.current = me.sessionTimeoutMin * 60 * 1000;
        }
      } catch (e) {}
    })();

    const logoutDueToInactivity = async () => {
      try {
        await apiFetch("/api/logout", { method: "POST" }).catch(() => {});
      } catch (e) {}
      logout && logout();
    };

    const resetTimer = () => {
      lastActiveRef.current = Date.now();
      try {
        localStorage.setItem("lastActive", String(lastActiveRef.current));
      } catch (e) {}
      if (activityTimer.current) clearTimeout(activityTimer.current);
      activityTimer.current = setTimeout(() => {
        logoutDueToInactivity();
      }, inactivityMsRef.current);
    };

    const events = [
      "mousemove",
      "mousedown",
      "keypress",
      "touchstart",
      "click",
    ];
    events.forEach((ev) => window.addEventListener(ev, resetTimer));
    resetTimer();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
      if (activityTimer.current) clearTimeout(activityTimer.current);
    };
  }, [user, navigate, logout]);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === "jwt_token" || e.key === "loggedInUser") {
        const token = localStorage.getItem("jwt_token");
        const lu = localStorage.getItem("loggedInUser");
        if (!token || !lu) {
          setUser(null);
          navigate("/login");
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate, setUser]);

  const fetchRecords = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const start = weekStart.toISOString().slice(0, 10);
      const end = weekEnd.toISOString().slice(0, 10);
      const url = `/api/timesheets/me?StartDate=${start}&EndDate=${end}`;
      const data = await apiFetch(url);
      data.sort(
        (a, b) => Date.parse(a.TimesheetDate) - Date.parse(b.TimesheetDate),
      );
      setRecords(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const start = weekStart.toISOString().slice(0, 10);
        const end = weekEnd.toISOString().slice(0, 10);
        const url = `/api/timesheets/me?StartDate=${start}&EndDate=${end}`;
        const data = await apiFetch(url);
        if (isMounted) {
          data.sort(
            (a, b) => Date.parse(a.TimesheetDate) - Date.parse(b.TimesheetDate),
          );
          setRecords(data);
        }
      } catch (e) {
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [user, weekStart, weekEnd, weekStartMs, weekEndMs]);

  if (!user) return null;

  return (
    <div className="main-layout">
      <div className="top-banner">
        <img src={bannerUrl} alt="Banner" />
      </div>
      <div className="body-container">
        <nav className="side-menu">
          <ul>
            <li
              className="menu-item"
              onClick={() => {
                fetchRecords();
                selectShown(panels.dashboard);
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="menu-icon" aria-hidden>
                  <img src="/dashboard-icon.svg" alt="" className="menu-icon" />
                </span>
                <span className="menu-label">Dashboard</span>
              </div>
              {dashboardOpen && <></>}
            </li>

            {/* Cloud Dashboard menu removed */}

            <li
              className="menu-item"
              onClick={() => setTimesheetsOpen((s) => !s)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="menu-icon" aria-hidden>
                  <img src="/timesheet-icon.svg" alt="" />
                </span>
                <span className="menu-label">Timesheets</span>
              </div>
              {timesheetsOpen && (
                <ul className="submenu">
                  <li
                    className="submenu-item"
                    onClick={() => {
                      setTimesheetsInitialDate(null);
                      selectShown(panels.timesheets);
                    }}
                  >
                    <span className="menu-icon" aria-hidden>
                      <img src="/timesheet-sub-icon.svg" alt="" />
                    </span>
                    <span style={{ marginLeft: 4 }}>Timesheets</span>
                  </li>
                  <li
                    className="submenu-item"
                    onClick={() => selectShown(panels.outlook)}
                  >
                    <span className="menu-icon" aria-hidden>
                      <img src="/outlook-sub-icon.svg" alt="" />
                    </span>
                    <span style={{ marginLeft: 4 }}>Outlook</span>
                  </li>
                  <li
                    className="submenu-item"
                    onClick={() => selectShown(panels.jira)}
                  >
                    <span className="menu-icon" aria-hidden>
                      <img src="/jira-sub-icon.svg" alt="" />
                    </span>
                    <span style={{ marginLeft: 4 }}>JIRA</span>
                  </li>
                </ul>
              )}
            </li>

            <li
              className="menu-item"
              onClick={() => selectShown(panels.profile)}
            >
              <span className="menu-icon" aria-hidden>
                <img src="/profile-icon.svg" alt="" />
              </span>
              <span className="menu-label">Profile</span>
            </li>

            {settingsEnabled && (
              <li
                className="menu-item"
                onClick={() => selectShown(panels.settings)}
              >
                <span className="menu-icon" aria-hidden>
                  <img src="/settings-icon.svg" alt="" />
                </span>
                <span className="menu-label">Settings</span>
              </li>
            )}

            {reportsEnabled && (
              <li className="menu-item">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="menu-icon" aria-hidden>
                    <img src="/reports-icon.svg" alt="" />
                  </span>
                  <span
                    className="menu-label"
                    onClick={() => setReportsOpen((s) => !s)}
                  >
                    Reports
                  </span>
                </div>
                {reportsOpen && (
                  <ul className="submenu">
                    <li
                      className="submenu-item"
                      onClick={() => {
                        setReportsView("clientSupport");
                        selectShown(panels.reports);
                      }}
                    >
                      <span className="menu-icon" aria-hidden>
                        <img src="/client-support-sub-icon.svg" alt="" />
                      </span>
                      <span style={{ marginLeft: 4 }}>Client Support</span>
                    </li>
                    <li
                      className="submenu-item"
                      onClick={() => {
                        setReportsView("projectOverview");
                        selectShown(panels.reports);
                      }}
                    >
                      <span className="menu-icon" aria-hidden>
                        <img src="/project-overview-sub-icon.svg" alt="" />
                      </span>
                      <span style={{ marginLeft: 4 }}>Project Overview</span>
                    </li>
                    <li
                      className="submenu-item"
                      onClick={() => {
                        setReportsView("sowProjects");
                        selectShown(panels.reports);
                      }}
                    >
                      <span className="menu-icon" aria-hidden>
                        <img src="/project-sow-sub-icon.svg" alt="" />
                      </span>
                      <span style={{ marginLeft: 4 }}>Project SOW's</span>
                    </li>
                    <li
                      className="submenu-item"
                      onClick={() => {
                        setReportsView("resourceOverview");
                        selectShown(panels.reports);
                        navigate("/main");
                      }}
                    >
                      <span className="menu-icon" aria-hidden>
                        <img src="/resource-overview-sub-icon.svg" alt="" />
                      </span>
                      <span style={{ marginLeft: 4 }}>Resource Overview</span>
                    </li>
                    {resourcePtoEnabled && (
                      <li
                        className="submenu-item"
                        onClick={() => {
                          setReportsView("resourcePTO");
                          selectShown(panels.reports);
                        }}
                      >
                        <span className="menu-icon" aria-hidden>
                          <img src="/resource-pto-sub-icon.svg" alt="" />
                        </span>
                        <span style={{ marginLeft: 4 }}>Resource PTO</span>
                      </li>
                    )}
                    <li
                      className="submenu-item"
                      onClick={() => {
                        setReportsView("resourceSplit");
                        selectShown(panels.reports);
                      }}
                    >
                      <span className="menu-icon" aria-hidden>
                        <img src="/resource-split-sub-icon.svg" alt="" />
                      </span>
                      <span style={{ marginLeft: 4 }}>Resource Split</span>
                    </li>
                    {analyticsEnabled && (
                      <li
                        className="submenu-item"
                        onClick={() => {
                          setReportsView("analytics");
                          selectShown(panels.reports);
                        }}
                      >
                        <span className="menu-icon" aria-hidden>
                          <img src="/analytics-sub-icon.svg" alt="" />
                        </span>
                        <span style={{ marginLeft: 4 }}>Analytics</span>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            <li
              className="menu-item logout-item"
              onClick={async () => {
                try {
                  await apiFetch("/api/logout", { method: "POST" });
                } catch (e) {}
                logout && logout();
              }}
            >
              <span className="menu-icon" aria-hidden>
                <img src="/logout-icon.svg" alt="" />
              </span>
              <span className="menu-label">Logout</span>
            </li>
          </ul>
          {cloudDashboardEnabled && (
            <ul className="submenu-section">
              <li
                className="menu-item"
                onClick={() => selectShown(panels.cloudDashboard)}
              >
                <span className="menu-icon" aria-hidden>
                  <img src="/cloud-icon.svg" alt="" />
                </span>
                <span className="menu-label">Cloud Dashboard</span>
              </li>
              <li
                className="menu-item"
                onClick={() => selectShown(panels.bitaDashboard)}
              >
                <span className="menu-icon" aria-hidden>
                  <img src="/bita-icon.svg" alt="" />
                </span>
                <span className="menu-label">BITA Dashboard</span>
              </li>
            </ul>
          )}
        </nav>
        <main className="main-panel">
          {showProfile ? (
            <ProfilePage onClose={() => setShowProfile(false)} />
          ) : showReports && reportsEnabled ? (
            reportsView === "sowProjects" ? (
              <ProjectBillable onClose={() => setShowReports(false)} />
            ) : reportsView === "projectOverview" ? (
              <ProjectOverview onClose={() => setShowReports(false)} />
            ) : reportsView === "resourceOverview" ? (
              <ResourceOverview onClose={() => setShowReports(false)} />
            ) : reportsView === "clientSupport" ? (
              <ClientSupport onClose={() => setShowReports(false)} />
            ) : reportsView === "resourcePTO" && resourcePtoEnabled ? (
              <ResourcePTO onClose={() => setShowReports(false)} />
            ) : reportsView === "resourceSplit" ? (
              <ResourceSplit onClose={() => setShowReports(false)} />
            ) : reportsView === "analytics" && analyticsEnabled ? (
              <AnalyticsPage
                user={user}
                onClose={() => setShowReports(false)}
              />
            ) : (
              <ReportsPage onClose={() => setShowReports(false)} />
            )
          ) : showSettings && settingsEnabled ? (
            <SettingsPage onClose={() => setShowSettings(false)} />
          ) : showTimesheets ? (
            <TimesheetsPage
              user={user}
              initialDate={timesheetsInitialDate || weekStart}
            />
          ) : showOutlook ? (
            <OutlookPage user={user} />
          ) : showJira ? (
            <JiraPage user={user} onClose={() => setShowJira(false)} />
          ) : showCloudDashboard && cloudDashboardEnabled ? (
            <CloudDashboard />
          ) : showBitaDashboard ? (
            <BitaDashboard />
          ) : (
            <>
              <h1>Welcome, {user.UserDescription}</h1>
              <p>Your role: {user.UserRole}</p>

              <div className="week-selector">
                <button
                  onClick={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setDate(newDate.getDate() - 7);
                    setSelectedDate(newDate);
                  }}
                >
                  ← Previous Week
                </button>

                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select a date"
                />

                <button
                  onClick={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setDate(newDate.getDate() + 7);
                    setSelectedDate(newDate);
                  }}
                >
                  Next Week →
                </button>
              </div>

              <div className="week-range-label">
                <strong>Week:</strong> {formatDateRange(weekStart, weekEnd)}
              </div>

              <div style={{ marginTop: 12 }}>
                <button
                  onClick={() => {
                    setTimesheetsInitialDate(weekStart);
                    setShowTimesheets(true);
                  }}
                >
                  Open Timesheets for this week
                </button>
              </div>

              {/* 📊 Chart and Table */}
              <div className="content-box">
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <div
                    style={{
                      alignSelf: "center",
                      width: "100%",
                      maxWidth: 900,
                    }}
                  >
                    {records.length > 0 && (
                      <TimeChart user={user} records={records} />
                    )}
                  </div>

                  <div style={{ alignSelf: "center" }}>
                    <button
                      className="dashboard-edit-btn"
                      onClick={() => {
                        setTimesheetsInitialDate(weekStart);
                        setShowTimesheets(true);
                      }}
                    >
                      Edit Timesheet
                    </button>
                  </div>

                  <div style={{ width: "100%" }}>
                    <TimeRecordsTable records={records} loading={loading} />
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default MainPage;
